/**
 * Pure decision for reconciling an EXTERNAL markdown `content` update into the
 * editor without clobbering the user's in-progress edits.
 *
 * This is the heart of the ENG-4453 data-loss fix, extracted from EluveEditor's
 * sync effect so the behavior is named, testable, and reviewable on its own (the
 * effect just performs whatever this returns).
 *
 * Inputs are all serialized with the SAME serializer
 * (getTiptapMarkdownContentFromEditor), so string comparison is a reliable
 * "did the user edit?" signal — unlike comparing against the raw stored string,
 * which v3's serializer may not byte-match.
 */
export type MarkdownSyncDecision =
  /** Leave the editor untouched (it's focused, or it holds unsaved user edits). */
  | { type: 'skip' }
  /** Editor already reflects the incoming content (echo / seeded); refresh baseline. */
  | { type: 'markClean' }
  /** Editor doesn't reflect the incoming content and has no unsaved edits; apply it. */
  | { type: 'apply' };

export const decideMarkdownSync = (args: {
  /** The editor's current content, serialized to markdown. */
  current: string;
  /** The editor's serialized content as of the last sync (null before first sync). */
  baseline: string | null;
  /** The incoming external `content` prop. */
  incoming: string;
  /** Whether the editor is focused (user actively in it). */
  isFocused: boolean;
}): MarkdownSyncDecision => {
  const { current, baseline, incoming, isFocused } = args;

  // Never reconcile while the user is actively typing.
  if (isFocused) return { type: 'skip' };

  // Editor already reflects the incoming content — an autosave echo, or it was
  // seeded with this exact content at mount. Just refresh the baseline.
  if (current === incoming) return { type: 'markClean' };

  // First sync (no baseline yet): the editor mounted before its content loaded.
  // Apply the arriving content ONLY if the editor is still empty (this is what
  // makes async-loaded content appear instead of staying blank). If the provider
  // already typed a draft into the blank editor — even if they've since blurred —
  // there's no baseline to compare against, so guard on emptiness and never
  // clobber that draft.
  if (baseline === null) {
    return current.trim() === '' ? { type: 'apply' } : { type: 'skip' };
  }

  // Established baseline: the editor diverged from it → unsaved user edits.
  // Never clobber them. (This is the data-loss guard.)
  if (current !== baseline) return { type: 'skip' };

  // Pristine and the external content genuinely changed → apply it.
  return { type: 'apply' };
};
